import React, { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import { api, inr, fmtIST } from "../lib/api";
import { useAuth } from "../lib/auth";
import Shell, { PageHeader } from "../components/Shell";
import { hasPerm } from "../components/Sidebar";
import { Money, HandCoins, ArrowDown, ArrowUp, X } from "@phosphor-icons/react";

const TYPE_BADGE = {
  sale: { label: "CASH SALE", cls: "bg-emerald-50 text-emerald-700" },
  refund: { label: "CASH REFUND", cls: "bg-red-50 text-red-700" },
  submission: { label: "SUBMITTED", cls: "bg-amber-50 text-amber-700" },
};

export default function CashRegister() {
  const { storeId } = useParams();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showSubmit, setShowSubmit] = useState(false);
  const canSubmit = hasPerm(user, "cash.submit");

  const load = useCallback(async (f = "", t = "") => {
    try {
      const params = new URLSearchParams();
      if (f) params.set("from_date", f);
      if (t) params.set("to_date", t);
      const r = await api.get(`/store/${storeId}/cash/register?${params.toString()}`);
      setData(r.data);
      setError("");
      if (!f) setFromDate(r.data.from);
      if (!t) setToDate(r.data.to);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  const applyFilter = () => load(fromDate, toDate);

  if (error) {
    return (
      <Shell>
        <PageHeader title="Cash Register" subtitle="Cash ledger for this store." testId="cash-header" />
        <div className="p-8"><div className="bg-red-50 border border-red-200 text-red-700 rounded-md p-4 text-sm" data-testid="cash-error">{error}</div></div>
      </Shell>
    );
  }

  return (
    <Shell>
      <PageHeader
        title="Cash Register"
        subtitle="Cash in hand, cash transactions and submissions to company."
        testId="cash-header"
        actions={canSubmit && (
          <button onClick={() => setShowSubmit(true)} data-testid="open-submit-cash" className="h-10 px-4 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md inline-flex items-center gap-1.5">
            <HandCoins size={15} weight="bold" /> Submit Cash
          </button>
        )}
      />
      <div className="p-8 space-y-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-slate-900 text-white rounded-md p-5" data-testid="cash-in-hand-card">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-400 font-semibold"><Money size={14} /> Cash in Hand</div>
            <div className="font-display text-3xl font-bold font-mono-num mt-2" data-testid="cash-in-hand-value">{inr(data?.cash_in_hand ?? 0)}</div>
            <div className="text-[10px] text-slate-400 mt-1">All-time · after submissions</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><ArrowDown size={14} className="text-emerald-600" /> Cash In (period)</div>
            <div className="font-display text-2xl font-bold font-mono-num mt-2 text-emerald-700" data-testid="period-cash-in">{inr(data?.period?.cash_in ?? 0)}</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><ArrowUp size={14} className="text-red-600" /> Cash Out (period)</div>
            <div className="font-display text-2xl font-bold font-mono-num mt-2 text-red-600" data-testid="period-cash-out">{inr(data?.period?.cash_out ?? 0)}</div>
            <div className="text-[10px] text-slate-400 mt-1">Refunds + submissions</div>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-5">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold"><HandCoins size={14} className="text-amber-600" /> Submitted to Company</div>
            <div className="font-display text-2xl font-bold font-mono-num mt-2 text-amber-700" data-testid="lifetime-submitted">{inr(data?.lifetime?.submitted ?? 0)}</div>
            <div className="text-[10px] text-slate-400 mt-1">All-time total</div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-4 flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">From</label>
            <input type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} data-testid="cash-from-date" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">To</label>
            <input type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} data-testid="cash-to-date" className="h-10 px-3 border border-slate-300 rounded-md text-sm font-mono-num" />
          </div>
          <button onClick={applyFilter} data-testid="cash-apply-filter" className="h-10 px-4 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-md">Apply</button>
          <div className="text-xs text-slate-400 pb-2.5">Showing last 3 days by default</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <table className="w-full text-sm" data-testid="cash-txn-table">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200 text-[10px] uppercase tracking-widest text-slate-500">
                <th className="text-left px-4 py-3 font-semibold">Date (IST)</th>
                <th className="text-left px-4 py-3 font-semibold">Type</th>
                <th className="text-left px-4 py-3 font-semibold">Reference</th>
                <th className="text-left px-4 py-3 font-semibold">Detail</th>
                <th className="text-left px-4 py-3 font-semibold">By</th>
                <th className="text-right px-4 py-3 font-semibold">Amount</th>
              </tr>
            </thead>
            <tbody>
              {(!data || data.transactions.length === 0) && (
                <tr><td colSpan={6} className="text-center py-12 text-slate-400" data-testid="cash-empty">No cash transactions in this period.</td></tr>
              )}
              {data?.transactions.map((t, i) => {
                const b = TYPE_BADGE[t.type] || { label: t.type.toUpperCase(), cls: "bg-slate-100 text-slate-600" };
                return (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-4 py-2.5 font-mono-num text-slate-600">{fmtIST(t.at)}</td>
                    <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold ${b.cls}`}>{b.label}</span></td>
                    <td className="px-4 py-2.5 font-mono-num font-semibold">{t.ref || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600 max-w-[240px] truncate">{t.detail || "—"}</td>
                    <td className="px-4 py-2.5 text-slate-600">{t.by || "—"}</td>
                    <td className={`px-4 py-2.5 text-right font-mono-num font-bold ${t.amount >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {t.amount >= 0 ? "+" : "−"}{inr(Math.abs(t.amount))}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showSubmit && (
        <SubmitCashModal
          cashInHand={data?.cash_in_hand ?? 0}
          onClose={() => setShowSubmit(false)}
          onDone={() => { setShowSubmit(false); load(fromDate, toDate); }}
          storeId={storeId}
        />
      )}
    </Shell>
  );
}

function SubmitCashModal({ storeId, cashInHand, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const save = async () => {
    const amt = parseFloat(amount) || 0;
    if (amt <= 0) { setError("Enter an amount greater than zero."); return; }
    setSaving(true); setError("");
    try {
      await api.post(`/store/${storeId}/cash/submit`, { amount: amt, notes });
      onDone();
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-md shadow-2xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="submit-cash-modal">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <div>
            <div className="font-display font-bold text-lg">Submit Cash to Company</div>
            <div className="text-xs text-slate-500">Records a cash handover and reduces cash in hand.</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-md"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex justify-between items-baseline">
            <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Cash in Hand</span>
            <span className="font-display font-bold text-lg font-mono-num" data-testid="modal-cash-in-hand">{inr(cashInHand)}</span>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Amount (₹) *</label>
            <input type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="submit-cash-amount" className="w-full h-11 px-3 border border-slate-300 rounded-md text-lg font-mono-num" placeholder="0.00" autoFocus />
            <button onClick={() => setAmount(String(cashInHand))} className="mt-1 text-[11px] font-semibold text-emerald-700 hover:underline" data-testid="submit-cash-full">Submit full amount ({inr(cashInHand)})</button>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest font-semibold text-slate-500 mb-1 block">Notes</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} data-testid="submit-cash-notes" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm resize-none" placeholder="e.g. Handed to Mr. Sharma / deposited in HDFC a/c" />
          </div>
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-md p-2" data-testid="submit-cash-error">{error}</div>}
        </div>
        <div className="px-5 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button onClick={onClose} className="h-10 px-4 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-md">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="submit-cash-save" className="h-10 px-5 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-bold rounded-md disabled:opacity-50 inline-flex items-center gap-1.5">
            <HandCoins size={14} weight="bold" /> {saving ? "Submitting…" : "Submit Cash"}
          </button>
        </div>
      </div>
    </div>
  );
}
